import * as React from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ContractFamilyRole, RoleUpdatePayload } from "@/types/contract-family";

export interface ParentOption {
  documentId: string;
  label: string;
}

interface Target {
  documentId: string;
  name: string | null;
  role: string;
  parentDocumentId?: string | null;
}

interface FormValues {
  role: ContractFamilyRole;
  parentDocumentId: string;
  cascadeChildrenToAmbiguous: boolean;
  comment: string;
}

interface Props {
  open: boolean;
  target: Target | null;
  parentOptions: ParentOption[];
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: RoleUpdatePayload) => Promise<void>;
}

const roles: ContractFamilyRole[] = ["MASTER", "CHILD", "AMENDMENT", "ANCILLARY", "NON_RELEVANT"];

const normalizeRole = (role: string): ContractFamilyRole =>
  roles.includes(role as ContractFamilyRole) ? (role as ContractFamilyRole) : "CHILD";

export const RoleOverrideDialog: React.FC<Props> = ({
  open,
  target,
  parentOptions,
  isSubmitting,
  onOpenChange,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      role: "CHILD",
      parentDocumentId: "",
      cascadeChildrenToAmbiguous: false,
      comment: "",
    },
  });

  const role = watch("role");
  const isMasterDemotion = target?.role === "MASTER" && role !== "MASTER";

  React.useEffect(() => {
    if (open && target) {
      reset({
        role: normalizeRole(target.role),
        parentDocumentId: target.parentDocumentId ?? "",
        cascadeChildrenToAmbiguous: false,
        comment: "",
      });
    }
  }, [open, reset, target]);

  const submit = async (values: FormValues) => {
    if (isMasterDemotion && !values.parentDocumentId && !values.cascadeChildrenToAmbiguous) {
      setError("root", {
        type: "manual",
        message: "Choose a replacement parent or cascade linked children to review.",
      });
      return;
    }
    clearErrors("root");
    await onSubmit({
      role: values.role,
      parent_document_id: values.role === "MASTER" ? null : values.parentDocumentId || null,
      cascade_children_to_ambiguous: isMasterDemotion ? values.cascadeChildrenToAmbiguous : undefined,
      comment: values.comment.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Change Role</DialogTitle>
          <DialogDescription>{target?.name || target?.documentId || "Selected document"}</DialogDescription>
        </DialogHeader>

        <form className="space-y-3" onSubmit={handleSubmit(submit)}>
          <label className="grid gap-1 text-xs font-medium text-slate-700">
            Role
            <select
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900"
              {...register("role")}
            >
              {roles.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1 text-xs font-medium text-slate-700">
            Parent
            <select
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 disabled:bg-slate-100"
              disabled={role === "MASTER"}
              {...register("parentDocumentId")}
            >
              <option value="">No parent selected</option>
              {parentOptions.map(option => (
                <option key={option.documentId} value={option.documentId}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {isMasterDemotion && (
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                {...register("cascadeChildrenToAmbiguous")}
              />
              Cascade linked children to ambiguous
            </label>
          )}

          {errors.root?.message ? (
            <p role="alert" className="text-xs text-red-700">
              {errors.root.message}
            </p>
          ) : null}

          <label className="grid gap-1 text-xs font-medium text-slate-700">
            Comment
            <Textarea className="min-h-20 text-sm" placeholder="Optional" {...register("comment")} />
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
